import { Grid } from '@mui/material';
import { ODSButton } from '@telekom-ods/react-ui-kit';

import useAppContext from '../../hooks/useAppContext';
import Button from '../Buttons/Button';
import { ODSSelect } from '../InputFields';
import Tooltip from '../Tooltip';

import IntroHeader from './IntroHeader';
import Search from './Search';
import TableStyles from './Table.module.css';
import type { DropDownSelectProps } from './utils';

import type {
  CreateAction,
  ExportData,
  HeaderActionSlot,
  TableField,
  TableRowData,
} from '@/types/tableTypes';

export interface HeaderProps {
  showHeader?: boolean;
  header: string;
  subHeader?: string;
  setFilter?: (filter: unknown) => void;
  setAdvancedFieldFilter?: (filter: Record<string, string | string[]>) => void;
  setCurrentPage: (page: number) => void;
  createAction?: CreateAction | null;
  headerActionSlot?: HeaderActionSlot | null;
  exportData?: ExportData | null;
  dropDownSelect?: DropDownSelectProps | null;
  search: boolean;
  rows: TableRowData[];
  setSearchedRows: (rows: TableRowData[]) => void;
  fields: TableField[];
  advancedSearch: boolean;
  enablePaginatedSearch?: boolean;
}

const Header = ({
  showHeader = true,
  header,
  subHeader = '',
  setFilter,
  setAdvancedFieldFilter,
  setCurrentPage,
  createAction = null,
  headerActionSlot = null,
  exportData = null,
  dropDownSelect = null,
  search,
  rows,
  setSearchedRows,
  fields,
  advancedSearch,
  enablePaginatedSearch = false,
}: HeaderProps) => {
  const { theme } = useAppContext();
  const hasTopRow = showHeader || !!headerActionSlot;
  const hasFiltersRow = !!createAction || !!exportData || !!dropDownSelect || search;

  return (
    <Grid
      container
      direction='column'
      pb={1}
      px={0}
      gap={1}
    >
      {hasTopRow && (
        <Grid
          container
          alignItems='center'
          justifyContent='space-between'
          wrap='nowrap'
        >
          {showHeader ? (
            <Grid item>
              <IntroHeader
                header={header}
                subHeader={subHeader}
                theme={theme}
              />
            </Grid>
          ) : (
            <Grid item />
          )}

          {headerActionSlot ? (
            <Grid
              item
              style={{
                display: 'flex',
                alignItems: 'center',
                marginLeft: '1rem',
              }}
            >
              {headerActionSlot.content}
            </Grid>
          ) : null}
        </Grid>
      )}

      {hasFiltersRow ? (
        <Grid
          item
          className={TableStyles.filters_wrapper ?? ''}
        >
          <Grid
            container
            alignItems='center'
            justifyContent='space-between'
            gap={1}
          >
            {createAction || exportData ? (
              <Grid
                item
                style={{
                  display: 'flex',
                  gap: '0.5rem',
                  alignItems: 'center',
                }}
              >
                {createAction && (
                  <Tooltip title={createAction.tooltip}>
                    <ODSButton
                      data-testid='button-Create'
                      disabled={createAction.disabled ?? false}
                      onClick={createAction.onClick}
                      variant={theme === 'light' ? 'secondary' : 'primary'}
                      label='Create'
                      size='small'
                      buttonIcon='add-type-standard'
                      leftIcon
                    />
                  </Tooltip>
                )}
                {exportData && (
                  <Tooltip title={exportData.tooltip}>
                    <Button
                      variant={theme === 'light' ? 'secondary' : 'primary'}
                      href={URL.createObjectURL(exportData.file)}
                      download={exportData.fileName}
                      label={exportData.name}
                    />
                  </Tooltip>
                )}
              </Grid>
            ) : null}

            {dropDownSelect && (
              <Grid item>
                <ODSSelect
                  name={dropDownSelect.name ?? ''}
                  label={dropDownSelect.label ?? ''}
                  value={dropDownSelect.value ?? ''}
                  onChange={selected => dropDownSelect.onChange?.(selected.value ?? '')}
                  menuItems={dropDownSelect.menuItems.map(item => ({
                    ...item,
                  }))}
                  style={{ marginBottom: 0 }}
                />
              </Grid>
            )}
            {search && (
              <Grid
                className={TableStyles.search_button_wrapper ?? ''}
                item
                component='div'
              >
                <Search
                  setCurrentPage={setCurrentPage}
                  rows={rows}
                  setSearchedRows={setSearchedRows}
                  fields={fields}
                  advancedSearch={advancedSearch}
                  enablePaginatedSearch={enablePaginatedSearch}
                  {...(setFilter && { setFilter })}
                  {...(setAdvancedFieldFilter && { setAdvancedFieldFilter })}
                />
              </Grid>
            )}
          </Grid>
        </Grid>
      ) : null}
    </Grid>
  );
};

export default Header;
